import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CheckCircle, Copy, ArrowLeft } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const BANK_DETAILS = {
  accountName: 'Rock Granites Pvt. Ltd.',
  accountNumber: '1234567890',
  ifscCode: 'HDFC0001234',
  bankName: 'HDFC Bank',
  branch: 'Chennai Main Branch',
};

const Field = ({ label, name, type = 'text', value, onChange, required, placeholder }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      placeholder={placeholder}
      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#111111] focus:border-transparent transition"
    />
  </div>
);

const Checkout = () => {
  const { cartItems, cartTotal, clearCart } = useCart();
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1 = details, 2 = payment, 3 = confirm
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState('');
  const [confirmedTotal, setConfirmedTotal] = useState(0);
  const [confirmedUTR, setConfirmedUTR] = useState('');
  const [form, setForm] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    country: '',
    address: '',
    paymentMethod: 'NEFT',
    utrNumber: '',
  });

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const copyToClipboard = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(''), 2000);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitting(true);

    // Snapshot before clearing cart
    const total = cartTotal;
    const utr = form.utrNumber;

    const order = {
      id: `RG-${Date.now()}`,
      customer: { ...form },
      items: cartItems,
      total,
      placedAt: new Date().toISOString(),
    };

    try {
      const existing = JSON.parse(localStorage.getItem('rg_orders') || '[]');
      existing.push(order);
      localStorage.setItem('rg_orders', JSON.stringify(existing));
    } catch {}

    setConfirmedTotal(total);
    setConfirmedUTR(utr);
    clearCart();
    setStep(3);
    setSubmitting(false);
  };

  if (cartItems.length === 0 && step !== 3) {
    return (
      <div className="min-h-screen pt-28 flex flex-col items-center justify-center text-center px-4">
        <p className="text-gray-500 mb-4">Your cart is empty.</p>
        <Link to="/products">
          <Button className="bg-[#111111] text-white hover:bg-[#2a2a2a]">Browse Products</Button>
        </Link>
      </div>
    );
  }

  // Step 3: Order Confirmed
  if (step === 3) {
    return (
      <div className="min-h-screen pt-28 flex flex-col items-center justify-center text-center px-4">
        <div className="max-w-md mx-auto">
          <CheckCircle size={64} className="text-green-500 mx-auto mb-4" />
          <h2 className="text-3xl font-bold text-[#111111] mb-3">Order Confirmed!</h2>
          <p className="text-gray-600 mb-2">
            Thank you, <strong>{form.name}</strong>. We've received your order and payment details.
          </p>
          <p className="text-gray-600 mb-6">
            Our team will verify your UTR number and confirm your order within <strong>24–48 hours</strong> via email at <strong>{form.email}</strong>.
          </p>
          <div className="bg-gray-50 rounded-lg p-4 text-left text-sm mb-6">
            <p className="font-medium text-gray-700 mb-1">Order Total</p>
            <p className="text-2xl font-bold text-[#111111]">₹{confirmedTotal.toLocaleString('en-IN')}</p>
            <p className="text-gray-500 mt-1">UTR: {confirmedUTR}</p>
          </div>
          <Link to="/products">
            <Button className="bg-[#111111] text-white hover:bg-[#2a2a2a]">Continue Shopping</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 bg-[#FAFAF8]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link to="/cart" className="text-gray-500 hover:text-[#111111] transition-colors">
            <ArrowLeft size={20} />
          </Link>
          <h1 className="text-3xl font-bold text-[#111111]">Checkout</h1>
        </div>

        {/* Steps Indicator */}
        <div className="flex items-center gap-2 mb-8">
          {['Customer Details', 'Payment'].map((label, i) => (
            <React.Fragment key={label}>
              <div className={`flex items-center gap-2 ${step > i + 1 ? 'text-green-600' : step === i + 1 ? 'text-[#111111]' : 'text-gray-400'}`}>
                <span className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-semibold border-2 ${step === i + 1 ? 'border-[#111111] bg-[#111111] text-white' : step > i + 1 ? 'border-green-500 bg-green-500 text-white' : 'border-gray-300'}`}>
                  {i + 1}
                </span>
                <span className="text-sm font-medium hidden sm:block">{label}</span>
              </div>
              {i < 1 && <div className="flex-1 h-px bg-gray-300 mx-2" />}
            </React.Fragment>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">

            {/* Step 1: Customer Details */}
            {step === 1 && (
              <Card>
                <CardContent className="p-6">
                  <h2 className="font-semibold text-xl text-[#111111] mb-6">Customer Details</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <Field label="Full Name" name="name" value={form.name} onChange={handleChange} required placeholder="John Smith" />
                    <Field label="Company Name" name="company" value={form.company} onChange={handleChange} required placeholder="ABC Constructions" />
                    <Field label="Email Address" name="email" type="email" value={form.email} onChange={handleChange} required placeholder="john@example.com" />
                    <Field label="Phone Number" name="phone" type="tel" value={form.phone} onChange={handleChange} required placeholder="+91 98765 43210" />
                    <Field label="Country" name="country" value={form.country} onChange={handleChange} required placeholder="India" />
                    <Field label="Shipping Address" name="address" value={form.address} onChange={handleChange} required placeholder="123 Main St, Chennai" />
                  </div>
                  <Button
                    size="lg"
                    className="w-full mt-6 bg-[#111111] text-white hover:bg-[#2a2a2a]"
                    onClick={() => {
                      if (!form.name || !form.company || !form.email || !form.phone || !form.country || !form.address) {
                        alert('Please fill in all required fields.');
                        return;
                      }
                      setStep(2);
                    }}
                  >
                    Continue to Payment
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Step 2: Payment */}
            {step === 2 && (
              <form onSubmit={handleSubmit}>
                <Card className="mb-5">
                  <CardContent className="p-6">
                    <h2 className="font-semibold text-xl text-[#111111] mb-2">Payment Method</h2>
                    <p className="text-sm text-gray-500 mb-5">We accept NEFT and RTGS bank transfers. Transfer the exact amount to the account below and enter your UTR number to confirm.</p>

                    {/* Payment method toggle */}
                    <div className="flex gap-3 mb-6">
                      {['NEFT', 'RTGS'].map(method => (
                        <button
                          key={method}
                          type="button"
                          onClick={() => setForm(f => ({ ...f, paymentMethod: method }))}
                          className={`flex-1 py-2.5 rounded-lg border-2 font-semibold text-sm transition-all ${form.paymentMethod === method ? 'border-[#111111] bg-[#111111] text-white' : 'border-gray-300 text-gray-600 hover:border-gray-400'}`}
                        >
                          {method}
                        </button>
                      ))}
                    </div>

                    {/* Bank Details */}
                    <div className="bg-gray-50 rounded-lg p-5 space-y-3">
                      <h3 className="font-semibold text-[#111111] text-sm mb-3">Bank Account Details</h3>
                      {Object.entries({
                        'Account Name': BANK_DETAILS.accountName,
                        'Account Number': BANK_DETAILS.accountNumber,
                        'IFSC Code': BANK_DETAILS.ifscCode,
                        'Bank': BANK_DETAILS.bankName,
                        'Branch': BANK_DETAILS.branch,
                      }).map(([key, val]) => (
                        <div key={key} className="flex justify-between items-center text-sm">
                          <span className="text-gray-500 w-36 flex-shrink-0">{key}</span>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-[#111111]">{val}</span>
                            {['Account Number', 'IFSC Code'].includes(key) && (
                              <button
                                type="button"
                                onClick={() => copyToClipboard(val, key)}
                                className="text-gray-400 hover:text-[#111111] transition-colors"
                              >
                                {copied === key ? <CheckCircle size={14} className="text-green-500" /> : <Copy size={14} />}
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="mt-5 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                      Transfer exactly <strong>₹{cartTotal.toLocaleString('en-IN')}</strong> to the account above, then enter your UTR number below.
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <h3 className="font-semibold text-lg text-[#111111] mb-4">Confirm Payment</h3>
                    <div className="mb-5">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        UTR / Reference Number <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="utrNumber"
                        value={form.utrNumber}
                        onChange={handleChange}
                        required
                        placeholder="e.g. HDFC12345678901234"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#111111] focus:border-transparent transition"
                      />
                      <p className="text-xs text-gray-400 mt-1">
                        Find this in your bank's transaction confirmation SMS or email.
                      </p>
                    </div>

                    <div className="flex gap-3">
                      <Button type="button" variant="outline" onClick={() => setStep(1)} className="flex-1">
                        Back
                      </Button>
                      <Button
                        type="submit"
                        size="lg"
                        className="flex-1 bg-[#111111] text-white hover:bg-[#2a2a2a]"
                        disabled={submitting}
                      >
                        {submitting ? 'Placing Order...' : 'Place Order'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </form>
            )}
          </div>

          {/* Order Summary Sidebar */}
          <div>
            <Card className="sticky top-24">
              <CardContent className="p-5">
                <h3 className="font-semibold text-[#111111] mb-4">Order Summary</h3>
                <div className="space-y-3 mb-4">
                  {cartItems.map(item => (
                    <div key={item.cartId} className="flex gap-3">
                      <img src={item.image} alt={item.productName} className="w-12 h-12 rounded object-cover flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-[#111111] leading-tight">{item.productName}</p>
                        <p className="text-xs text-gray-500">{item.finish} · {item.thickness}</p>
                        <p className="text-xs text-gray-500">{item.quantity} sq ft</p>
                      </div>
                      <span className="text-xs font-semibold text-[#111111] flex-shrink-0">
                        ₹{(item.pricePerSqFt * item.quantity).toLocaleString('en-IN')}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="border-t border-gray-200 pt-3">
                  <div className="flex justify-between font-bold text-[#111111]">
                    <span>Total</span>
                    <span>₹{cartTotal.toLocaleString('en-IN')}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
